/* eslint-disable */
import type { FullNameSource } from "../cash/customers/CustomerFullNameProto.pb";
import type { Country } from "../common/countries.pb";
import type { Money } from "../common/currency.pb";
import type { AddressLink } from "./accounts.pb";
import type { Alias } from "./alias.pb";
import type { BankAccount } from "./bank_account.pb";
import type { ComplianceState } from "./compliance_state.pb";
import type { CustomerProfile } from "./customer_profile.pb";
import type { DepositMechanism } from "./deposit_mechanism.pb";
import type { IdentityVerificationAttempt } from "./identity.pb";
import type { KycState } from "./kyc_state.pb";
import type { PaymentCard } from "./payment_card.pb";
import type { RatePlan } from "./rate_plan.pb";
import type { Region } from "./region.pb";
import type { StatusLevel } from "./status_level.pb";
import type { StoredValue } from "./stored_value.pb";
import type { SuspensionCategory } from "./suspension.pb";

/**
 * A domain that limits what a customer entity can do. Note that 'customer' in
 * this is strictly a customer entity; people who use Cash App may have
 * multiple customer entities for their various uses of our product, each with
 * their own limitations.
 *
 * Sometimes we want to accept a debit card number before we confirm the email
 * address it is being linked to. Linking it directly is dangerous: an attacker
 * could link her debit card to a victim's email address to steal his incoming
 * payments.
 *
 * Scopes limit where a customer's debit card number (and potentially other
 * information) to the domain in which it was created. A customer who enters a
 * debit card number in an app can use that debit card number in that app. But
 * she can't use it for email until the linked email address has been confirmed.
 *
 * Similarly, a customer who enters a debit card number for payroll of a bakery
 * can use that card number for payroll of that bakery, and payroll of that
 * bakery only.
 *
 * When we confirm that it's one person controls multiple customer entities, we
 * may share the debit card linked by those entities to save the customer from
 * re-entering that information.
 */
export enum Scope {
  /**
   * EMAIL - In this scope we require a customer prove ownership of an email address to
   * send and receive payments. This happens either explicitly via confirmation
   * emails, or implicitly by spoof checking, and by other actions originating
   * from email.
   */
  EMAIL = "EMAIL",
  /**
   * APP_UNUSED - App-created customers. Unused as of January 2014 because our apps don't yet
   * create customers without confirming email first.
   */
  APP_UNUSED = "APP_UNUSED",
  /**
   * TPP - Third-party payments (TPP) such as payroll. Payments made by customers in
   * this scope must be initiated by the same third-party service that created
   * the TPP customer.
   */
  TPP = "TPP",
}

/** Reasons provided by the customer during account verification for using Cash App. */
export enum UsageReason {
  PERSONAL = "PERSONAL",
  COMMERCIAL = "COMMERCIAL",
  TIPPING = "TIPPING",
}

/** Represents a Franklin customer. */
export interface Customer {
  /** This customer's Franklin customer token. */
  customer_token?: string;
  scope?: Scope;
  scope_token?: string;
  /**
   * Summary field. Deprecated in favor of aliases which contains all owned email addresses.
   *
   * @deprecated
   */
  email_address?: string;
  bank_account?: BankAccount;
  payment_card?: PaymentCard;
  credit_card?: PaymentCard;
  stored_value?: StoredValue;
  /** stored_values is a superset of stored_value (i.e. will always contain at least stored_value). */
  stored_values?: StoredValue[];
  /**
   * Only set if all the customer's cards are currently unlinked. Used by risk to make
   * decisions when payments do not involve any cards because of stored values.
   */
  last_known_card?: PaymentCard;
  /** Summary field.  This includes both failed card links and failed bank account links. */
  failed_card_links?: number;
  /** Summary field */
  blacklisted?: boolean;
  /** Summary field */
  suspended?: boolean;
  suspension_category?: SuspensionCategory;
  customer_profile?: CustomerProfile;
  /**
   * Compliance state deals with personal account verification and uses an IDV provider to decide
   * what state to set.
   *  - NEW: New customers (haven't required an IDV verification yet due to e.g. low payment volume).
   *  - FORCE_UPGRADE: We require IDV verification.
   *  - APPROVED: Customer has passed IDV verification.
   *  - BLACKLISTED: Permanently fail payments if they permanently fail IDV.
   */
  compliance_state?: ComplianceState;
  /** @deprecated */
  email_domain?: string;
  /** @deprecated */
  invited_by_user_token?: string;
  /** @deprecated */
  invited_by_customer_token?: string;
  /**
   * Summary field. Deprecated in favor of aliases which contains all owned SMS numbers.
   *
   * @deprecated
   */
  sms_number?: string;
  /** @deprecated */
  sms_notifications_enabled?: boolean;
  /**
   * Obsolete. Always null.
   *
   * @deprecated
   */
  deposit_mechanism?: DepositMechanism;
  status_level?: StatusLevel;
  usage_reason?: UsageReason;
  rate_plan?: RatePlan;
  /** Unix time in millis */
  rate_plan_accepted_at?: number;
  forced_business_at?: number;
  id?: string;
  active_customer_id?: string;
  full_name?: string;
  full_name_source?: FullNameSource;
  all_full_names_used?: string[];
  /** @deprecated */
  invited_by_id?: string;
  /** Summary field. This allows us to detect potential misconfigurations of customers' email apps */
  spoofed_as_sender_count?: number;
  /** Summary field. This allows us to detect bad customers trying to be the recipient of spoofed emails */
  spoofed_as_recipient_count?: number;
  /** @deprecated */
  referred_by_id?: string;
  /** Unix time in millis */
  created_at?: number;
  /** Unix time in millis */
  updated_at?: number;
  /** When the very first money moving mechanism was successfully linked for this customer. */
  initial_instrument_linked_at?: number;
  /** Summary field. Contains the set of owned email addresses, SMS numbers and apps. */
  aliases?: Alias[];
  /** KnowYourCustomer State of the customer */
  kyc_state?: KycState;
  /**
   * In APIs that allow callers to request customers by token, this field is populated with the
   * customer token used in the request if the found customer is inactive and the active customer
   * is returned instead.
   */
  original_customer_token?: string;
  /** @deprecated */
  tlo_response?: TloResponse;
  /**
   * All the attempts this customer has made to verify her identity.
   *
   * @deprecated
   */
  idv_attempts?: IdentityVerificationAttempt[];
  /**
   * Non-null if the Customer has a business account and their MCC has been set.
   * The MCC is used when auth+capturing the payments towards this customer. Customers with a null
   * MCC always default to 7299 on the pull.
   */
  mcc?: string;
  personal_address_1?: string;
  personal_address_2?: string;
  personal_address_3?: string;
  personal_zip?: string;
  personal_city?: string;
  personal_state?: string;
  country_code?: Country;
  /** Whether the customer has a passcode set. */
  has_passcode?: boolean;
  /**
   * When the customer became a Cash customer.
   * Not set if the customer is not yet considered a Cash customer.
   */
  customer_since?: number;
  /** Unix time in millis */
  closed_at?: number;
  /** @deprecated */
  government_ids?: GovernmentId[];
  /** When the customer was denylisted. */
  blacklisted_at?: number;
  addresses?: AddressLink[];
  region?: Region;
  is_business?: boolean;
  /** Denotes if a customer is backing a Pay with Cash App merchant. */
  is_pwc_merchant?: boolean;
  /** Merchant token for the Pay with Cash App merchant in merchantein. */
  pwc_merchant_token?: string;
  /** Display name for the Pay with Cash App merchant. */
  pwc_merchant_name?: string;
  /** SQ Seller Merchant ID for PWC Seller merchants */
  seller_external_merchant_id?: string;
  bitcoin_limits?: BitcoinLimits;
  signed_card_disclosure_at?: number;
}

export interface TloResponse {
  customer_id?: string;
  customer_token?: string;
  created_at?: number;
  tlo_result?: TloResponseTloResult;
  /** Person data as returned by TLO. */
  tlo_verified_person?: TloVerifiedPerson;
  /** True if the upgrade was approved by Frisky. */
  frisky_approved?: boolean;
  /** Frisky's reasoning for denying the upgrade. */
  frisky_reason?: string;
}

export enum TloResponseTloResult {
  /** SUCCESS - TLO identified this customer and Frisky approved. */
  SUCCESS = "SUCCESS",
  /**
   * NOT_APPROVED_TLO - A hard rejection from TLO. Likely bogus data from the customer. Retries
   * for this customer may be permitted, depending on how many other attempts
   * have been made.
   */
  NOT_APPROVED_TLO = "NOT_APPROVED_TLO",
}

export interface GovernmentId {
  /** Unix time in millis */
  government_id_token?: string;
  created_at?: number;
  id_number?: string;
  /** Unix time in millis */
  birth_date?: number;
  first_name?: string;
  middle_name?: string;
  last_name?: string;
  name_suffix?: string;
  address?: string;
  city?: string;
  postal_code?: string;
  state?: string;
  barcode_text?: string;
  front_image_trunk_token?: string;
  back_image_trunk_token?: string;
  /** Unix time in millis */
  expiration_date?: number;
  fidelius_token?: string;
  verification_result?: GovernmentIdVerificationResult;
  barcode_text_source?: GovernmentIdBarcodeTextSource;
}

export enum GovernmentIdVerificationResult {
  /** VERIFIED - The ID was successfully verified. */
  VERIFIED = "VERIFIED",
  /** FAILED_TO_DECODE - The server was unable to decode the barcode from the back image. */
  FAILED_TO_DECODE = "FAILED_TO_DECODE",
  /**
   * FAILED_TO_PARSE - The server was unable to parse a required field from the barcode text, such as last name,
   * birth date, or address.
   */
  FAILED_TO_PARSE = "FAILED_TO_PARSE",
  /** ID_EXPIRED - The ID was expired. */
  ID_EXPIRED = "ID_EXPIRED",
  /** REGION_UNSUPPORTED - The ID was from a country or region that is not supported (outside the 50 states and DC). */
  REGION_UNSUPPORTED = "REGION_UNSUPPORTED",
  /** UNDER_AGE - The ID birth date was under 18 years old. */
  UNDER_AGE = "UNDER_AGE",
  /**
   * REJECTED - The ID was originally VERIFIED but was reviewed and rejected by a human. IDs can be rejected
   * for a variety of reasons including inconsistency with other customer data or blurriness.
   */
  REJECTED = "REJECTED",
  /** MISSING_TRUNK_TOKEN - The server was unable to store valid Trunk token(s) of the image(s) of government ID. */
  MISSING_TRUNK_TOKEN = "MISSING_TRUNK_TOKEN",
  /** UNSUPPORTED_ID_CLASS - The government id provided is not supported for identity verification */
  UNSUPPORTED_ID_CLASS = "UNSUPPORTED_ID_CLASS",
}

export enum GovernmentIdBarcodeTextSource {
  /** SERVER - The barcode text was derived on the server by decoding the back image. */
  SERVER = "SERVER",
  /**
   * CLIENT - The barcode text was provided by the client and we were unable to decode it from the back
   * image on the server. This is inherently dangerous as it is easier to spoof than the image.
   */
  CLIENT = "CLIENT",
  /** UNKNOWN - For historical data, we weren't recording the source of the barcode text. */
  UNKNOWN = "UNKNOWN",
}

/** Represents the TLO verification data for a Franklin customer. */
export interface TloVerifiedPerson {
  full_name?: string;
  birthdate?: number;
  name_score?: number;
  ssn_score?: number;
  birthdate_score?: number;
  total_score?: number;
  first_name_matches?: boolean;
  middle_name_matches?: boolean;
  last_name_matches?: boolean;
  partial_ssn_matches?: boolean;
  birthdate_year_matches?: boolean;
  birthdate_month_matches?: boolean;
  birthdate_day_matches?: boolean;
  /** TLO's guess of the customer's SSN. */
  ssn_fidelius_token?: string;
}

/** Encapsulates the PII data collected about a Franklin customer. */
export interface CustomerPiiData {
  full_name?: string;
  email_address?: string;
  sms_number?: string;
}

/** Encapsulatest the PII address data returned by TLO */
export interface TloAddressResultPiiData {
  sms_number?: string;
  street_address1?: string;
  street_address2?: string;
  street_address3?: string;
  city?: string;
  state?: string;
  zip?: string;
}

/** Represents Bitcoin deposit and withdrawal limits */
export interface BitcoinLimits {
  /** Weekly Bitcoin Deposit Limit in USD */
  weeklyDepositLimit?: Money;
  /** Weekly Bitcoin Withdrawal Limit USD */
  weeklyWithdrawalLimit?: Money;
  /** Daily Withdrawal Limit in USD */
  dailyWithdrawalLimit?: Money;
  description?: string;
}
